import { useNavigate, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import {
  useEditarDocumentoMutation,
  useGetDocsListQuery,
  useGetEnviarWalkawayMutation,
  useGetValidacionMutation,
  useLazyGetDocsListQuery,
} from "../../../services/docs";
import { ItemDocumentoCarga } from "../../../components/ItemDocumentoCarga";
import { Spinner } from "../../../components/Spinner";

export const CargaDocumentosScreen = () => {
  const { siniestro, tramite_id } = useParams();
  const navigate = useNavigate();

  // Queries & Mutations
  const { data, isLoading } = useGetDocsListQuery({
    siniestro,
    tramite: tramite_id,
  });
  const [triggerList] = useLazyGetDocsListQuery();
  const [validarSend, { isLoading: isLoadingCarga }] = useGetValidacionMutation();
  const [walkawaySend, { isLoading: isLoadingWalkaCarga }] = useGetEnviarWalkawayMutation();
  const [eliminar, { isLoading: eliminarLoading }] = useEditarDocumentoMutation();

  // Funciones de Acción
  const eliminarDocumento = async (doc: any) => {
    try {
      const resp = await eliminar({
        fc_siniestro: siniestro,
        fi_tramite_id: +(tramite_id || 0),
        fi_documento_id: doc.ref_fi_documento_id,
        fi_documento_activo: 1,
      }).unwrap();

      if (resp) {
        toastr.success(resp.message || "Documento reactivado correctamente");
        triggerList({ siniestro, tramite: tramite_id });
      }
    } catch (err: any) {
      toastr.error(err?.data?.message || "Error al reactivar el documento");
    }
  };

  const enviarAValidar = async () => {
    try {
      const resp = await validarSend({
        siniestro,
        tramite_id: +(tramite_id || 0),
      }).unwrap();

      if (resp) {
        toastr.info(resp.message || "Trámite enviado a validación");
        navigate(`/view/siniestro/${siniestro}/${tramite_id}`);
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error("Ocurrió un error al intentar validar");
      }
    }
  };

  const enviarWalkaway = async () => {
    try {
      const resp = await walkawaySend({
        siniestro,
        tramite_id: +(tramite_id || 0),
      }).unwrap();

      if (resp) {
        toastr.info(resp.message || "Trámite enviado a Walkaway");
        navigate(`/view/siniestro/${siniestro}/${tramite_id}`);
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error("Ocurrió un error al enviar a Walkaway");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const isProcesando = isLoadingCarga || isLoadingWalkaCarga;

  return (
    <div className="space-y-6">

      {/* --- Barra de Acción Global y Leyenda --- */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col items-center justify-between gap-4 border-b border-slate-100 p-4 sm:flex-row">
          <div className="flex items-center gap-3 text-sm font-bold text-slate-800">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
            </div>
            Gestión de Carga de Expediente
          </div>

          {/* Botones de Acción Global */}
          <div className="flex flex-wrap items-center justify-end gap-3 w-full sm:w-auto">
            {data?.validacion && (
              <button
                onClick={enviarAValidar}
                disabled={isProcesando}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-brand-600/30 transition hover:bg-brand-700 disabled:opacity-50"
              >
                {isLoadingCarga ? <Spinner /> : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                    Enviar a Validar
                  </>
                )}
              </button>
            )}

            {data?.btnWalkaway && (
              <button
                onClick={enviarWalkaway}
                disabled={isProcesando}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
              >
                {isLoadingWalkaCarga ? <Spinner /> : (
                  <>
                    <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                    Procesar Walkaway
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Leyenda de Códigos de Estatus */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50/50 px-5 py-3 text-xs">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Código de Estatus:
          </span>
          <div className="flex flex-wrap items-center gap-5">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-slate-400 ring-2 ring-slate-100"></span>
              <span className="font-medium text-slate-600">Documento No Cargado</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-100"></span>
              <span className="font-medium text-slate-700">Documento Cargado</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-brand-500 ring-2 ring-brand-100"></span>
              <span className="font-medium text-slate-700">Digital Verificado</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-amber-100"></span>
              <span className="font-medium text-slate-700">Digital Rechazado</span>
            </div>
          </div>
        </div>
      </section>

      {/* --- Grid: Documentos Titular --- */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="h-4 w-1.5 rounded bg-brand-600"></div>
          <h3 className="text-sm font-bold tracking-tight text-slate-800">
            Documentos del Titular
          </h3>
          <span className="text-xs font-normal text-slate-400">
            ({data?.documentos?.length || 0} requerimientos)
          </span>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {data?.documentos?.map((doc: any, i: number) => (
            <ItemDocumentoCarga
              key={doc.id}
              doc={{ ...doc, cargado: false }}
              i={i}
              siniestro={siniestro}
              tramite_id={tramite_id}
              doc_adicional={data.adicionales}
            />
          ))}
        </div>
      </section>

      {/* --- Grid: Documentos Adicionales --- */}
      {data?.adicionales && data.adicionales.length > 0 && (
        <section className="space-y-4 pt-4">
          <div className="flex items-center gap-2 border-t border-slate-200 pt-6">
            <div className="h-4 w-1.5 rounded bg-blue-400"></div>
            <h3 className="text-sm font-bold tracking-tight text-slate-800">
              Documentación Adicional / Especial
            </h3>
            <span className="text-xs font-normal text-slate-400">
              ({data.adicionales.length} requerimientos)
            </span>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {data.adicionales.map((doc: any, i: number) => (
              <ItemDocumentoCarga
                key={doc.id}
                doc={{ ...doc, cargado: false }}
                i={i}
                siniestro={siniestro}
                tramite_id={tramite_id}
                doc_adicional={data.adicionales}
              />
            ))}
          </div>
        </section>
      )}

      {/* --- Grid: Documentos Eliminados (Papelera) --- */}
      {data?.documentosBorrados && data.documentosBorrados.length > 0 && (
        <section className="space-y-4 pt-4">
          <div className="flex items-center gap-2 border-t border-slate-200 pt-6">
            <div className="h-4 w-1.5 rounded bg-rose-400"></div>
            <h3 className="text-sm font-bold tracking-tight text-slate-800">
              Documentos Inactivos / Eliminados
            </h3>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.documentosBorrados.map((doc: any) => (
              <div
                key={doc.id}
                className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-slate-300"
              >
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-500">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                  </div>
                  <div className="truncate text-xs font-semibold text-slate-700">
                    {doc.dc_fc_nombre_documento}
                  </div>
                </div>

                <div className="shrink-0 pl-2">
                  <button
                    onClick={() => eliminarDocumento(doc)}
                    disabled={eliminarLoading}
                    className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-[11px] font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-brand-600 disabled:opacity-50"
                  >
                    {eliminarLoading ? <Spinner /> : "Reactivar"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

    </div>
  );
};
